import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../../components/Button';
import { RemarkAppendFields } from '../../../components/RemarkAppendFields';
import { installmentCardService } from '../../../services/installment-card.service';
import { type Installment, installmentService } from '../../../services/installment.service';
import { irrService } from '../../../services/irr.service';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { findInstallmentToPostpone } from './findInstallmentToPostpone';
import { clearPostponeCheck, getPostponeCheck, subscribePostponeCheck } from './postponePromptStore';

const REMARK_MAX = 500;
/** Last selectable day, counted from today; the backend allows strictly less than now + 31 days. */
const MAX_DAYS_AHEAD = 30;

function toDateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function daysFromToday(days: number): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
}

/** `YYYY-MM-DD` as local midnight, or null when malformed. */
function parseDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isFinite(d.getTime()) ? d : null;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR', minimumFractionDigits: 2 }).format(amount);

const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString() : '-');

function collectibleAmount(installment: Installment): number {
  const balance = Number(installment.balance);
  if (Number.isFinite(balance) && balance > 0) return Math.round(balance);
  const fee = Number(installment.installmentFee);
  return Number.isFinite(fee) ? Math.round(fee) : 0;
}

/**
 * App-level prompt: after an installment payment, offers to postpone the card's earliest NEUTRAL
 * installment that is due before tomorrow.
 */
export const InstallmentPostponePromptHost: React.FC = () => {
  const { t } = useTranslation();
  const { request, version } = useSyncExternalStore(subscribePostponeCheck, getPostponeCheck);
  const [installment, setInstallment] = useState<Installment | null>(null);
  const [currentCardRemark, setCurrentCardRemark] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [remarkAppend, setRemarkAppend] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setInstallment(null);
    setCurrentCardRemark('');
    if (!request) return;
    let cancelled = false;
    void Promise.all([
      installmentService.getInstallmentsByInstallmentCardId(request.installmentCardId),
      installmentCardService.getInstallmentCard(request.installmentCardId).catch(() => null),
    ])
      .then(([response, card]) => {
        if (cancelled) return;
        const candidate = findInstallmentToPostpone(response.data?.installments ?? []);
        if (!candidate) {
          clearPostponeCheck();
          return;
        }
        setDateValue(toDateInputValue(daysFromToday(1)));
        setRemarkAppend('');
        setFormError(null);
        setSubmitting(false);
        setInstallment(candidate);
        setCurrentCardRemark(String(card?.remark ?? '').trimEnd());
      })
      .catch((err) => {
        console.error('[POSTPONE-PROMPT] Failed to load installments:', err);
        if (!cancelled) clearPostponeCheck();
      });
    return () => {
      cancelled = true;
    };
  }, [request, version]);

  const close = () => {
    if (submitting) return;
    clearPostponeCheck();
  };

  const handleSubmit = async () => {
    if (!request || !installment) return;
    setFormError(null);
    const remarkTrim = remarkAppend.trim();
    if (!remarkTrim) {
      setFormError(t('installmentCard.postponePrompt.remarkRequired'));
      return;
    }
    if (remarkTrim.length > REMARK_MAX) {
      setFormError(t('installmentCard.postponePrompt.remarkTooLong', { max: REMARK_MAX }));
      return;
    }
    const date = parseDateInput(dateValue);
    if (!date) {
      setFormError(t('installmentCard.postponePrompt.invalidDate'));
      return;
    }
    if (date < daysFromToday(1) || date > daysFromToday(MAX_DAYS_AHEAD)) {
      setFormError(t('installmentCard.postponePrompt.dateRange', { days: MAX_DAYS_AHEAD }));
      return;
    }

    setSubmitting(true);
    try {
      const result = await irrService.postponeCardInstallment({
        ICid: request.installmentCardId,
        installmentId: installment.installmentId,
        postponedDate: date.toISOString(),
        cardRemark: remarkTrim,
        ...(request.irrId ? { irrId: request.irrId } : {}),
      });
      toast.success(
        t(
          result?.recordedOnIrr ? 'installmentCard.postponePrompt.successOnIrr' : 'installmentCard.postponePrompt.success',
          { id: installment.installmentId, date: date.toLocaleDateString() },
        ),
      );
      request.onPostponed?.();
      setSubmitting(false);
      clearPostponeCheck();
    } catch (err) {
      setFormError(getApiErrorMessage(err, t('installmentCard.postponePrompt.failed')));
      setSubmitting(false);
    }
  };

  const open = Boolean(request && installment);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog className="relative z-60" onClose={close}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-xl">
              {installment && request ? (
                <>
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {t('installmentCard.postponePrompt.title')}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('installmentCard.postponePrompt.subtitle', { icid: request.installmentCardId })}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">{t('installmentCard.installmentId') || 'Installment ID'}</p>
                      <p className="font-mono font-medium text-gray-900">{installment.installmentId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('installmentCard.paymentDate') || 'Payment Date'}</p>
                      <p className="font-medium text-gray-900">{formatDate(installment.paymentdate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('installmentCard.installmentFee') || 'Installment Fee'}</p>
                      <p className="font-medium text-gray-900">{formatCurrency(Number(installment.installmentFee) || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('installmentCard.postponePrompt.collectible')}</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(collectibleAmount(installment))}</p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="installment-postpone-date">
                      {t('installmentCard.postponePrompt.dateLabel')}
                    </label>
                    <input
                      id="installment-postpone-date"
                      type="date"
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                      value={dateValue}
                      min={toDateInputValue(daysFromToday(1))}
                      max={toDateInputValue(daysFromToday(MAX_DAYS_AHEAD))}
                      onChange={(e) => setDateValue(e.target.value)}
                      disabled={submitting}
                    />
                    <p className="text-xs text-gray-500">
                      {t('installmentCard.postponePrompt.dateHint', { days: MAX_DAYS_AHEAD })}
                    </p>
                  </div>

                  <RemarkAppendFields
                    id="installment-postpone-remark"
                    currentRemark={currentCardRemark}
                    appendValue={remarkAppend}
                    onAppendChange={setRemarkAppend}
                    required
                    disabled={submitting}
                    appendLabel={`${t('installmentCard.postponePrompt.remarkLabel')} (${remarkAppend.trim().length}/${REMARK_MAX})`}
                    placeholder={t('installmentCard.postponePrompt.remarkPlaceholder')}
                    rows={3}
                  />
                  <p className="text-xs text-gray-500">{t('installmentCard.postponePrompt.effectHint')}</p>

                  {formError ? <p className="text-sm text-red-600">{formError}</p> : null}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={close} disabled={submitting}>
                      {t('installmentCard.postponePrompt.skip')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => void handleSubmit()}
                      disabled={submitting}
                      className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                    >
                      {submitting ? t('common.saving') : t('installmentCard.postponePrompt.submit')}
                    </Button>
                  </div>
                </>
              ) : null}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InstallmentPostponePromptHost;
